import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  AlertTriangle,
  BadgeCheck,
  CalendarClock,
  ClipboardList,
  Eye,
  FileSpreadsheet,
  Hourglass,
  Inbox,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
  Wrench,
} from "lucide-react";
import Layout from "../../layout/Layout";
import { useAuth } from "../../../components/context/AuthContext";
import { getDofIstekler, deleteDofIstek } from "../../../api/kalite/dofApi";
import {
  ASSIGNMENT_LABELS,
  FALIYET_LABELS,
  DOF_STATUSES,
  daysUntil,
  formatDofDate,
  getDofStatus,
  getStatusMeta,
  getTespitTuruMetni,
  initials,
} from "./dofFormUtils";
import "./Dof.css";

const SCOPES = [
  { key: "pending", label: "Bekleyen İşlerim", icon: Inbox },
  { key: "all", label: "Tümü", icon: ClipboardList },
  { key: "assigned", label: "Bana Atananlar", icon: BadgeCheck },
  { key: "created", label: "Açtıklarım", icon: Plus },
];

const STATUS_ICONS = {
  acik: Wrench,
  onay_bekliyor: Hourglass,
  takipte: ShieldCheck,
  kapali: BadgeCheck,
};

const getAssignedText = (dof, type) =>
  (dof.assignedUsers || [])
    .filter((item) => !type || item.assignmentType === type)
    .map((item) => item.fullName)
    .filter(Boolean)
    .join(", ");

const isLate = (dof) => !!(dof._termin?.overdue || dof._termin?.followUpLate);

function TerminCell({ dof }) {
  const status = getDofStatus(dof);
  const t = dof._termin || {};
  if (status === "kapali") {
    return (
      <>
        <span>{formatDofDate(dof.dofKapatmaTarihiOnay)}</span>
        <span className="dof-sub">kapatıldı</span>
      </>
    );
  }
  const date = status === "takipte" ? t.lastDue : t.nextDue;
  if (!date) return <span className="dof-sub">Termin girilmedi</span>;
  const days = daysUntil(date);

  let cls = "dof-badge-muted";
  let text = days === 0 ? "Bugün" : days !== null && days > 0 ? `${days} gün kaldı` : "";
  if (status === "acik" && t.overdue) {
    cls = "st-gecikmis";
    text = "Termin geçti";
  } else if (status === "acik" && days !== null && days <= 3) {
    cls = "st-acik";
  } else if (status === "takipte") {
    if (t.followUpLate) {
      cls = "st-gecikmis";
      text = "Takip gecikti";
    } else if (t.followUpDue) {
      cls = "st-takipte";
      text = "Takip zamanı";
    } else {
      text = days !== null ? `Takibe ${days} gün` : "";
    }
  }
  return (
    <>
      <span>{formatDofDate(date)}</span>
      {text && (
        <div>
          <span className={`dof-badge ${cls}`} style={{ marginTop: 4 }}>
            <CalendarClock size={12} /> {text}
          </span>
        </div>
      )}
    </>
  );
}

function DofList() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [dofs, setDofs] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);

  const [scope, setScope] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  const fetchDofs = useCallback(async (selectedScope) => {
    try {
      setLoading(true);
      const response = await getDofIstekler(selectedScope);
      if (response?.data?.success) {
        setDofs(Array.isArray(response.data.data) ? response.data.data : []);
        setMeta(response.data.meta || {});
      }
    } catch (error) {
      console.error("DÖF verileri çekilirken hata oluştu:", error);
      alert(error.response?.data?.message || "Veriler getirilemedi.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDofs(scope);
  }, [scope, fetchDofs]);

  // Tüm DÖF'leri görme yetkisi olmayan kullanıcıyı, bekleyen işi varsa doğrudan "Bekleyen İşlerim"e al
  const [autoScoped, setAutoScoped] = useState(false);
  useEffect(() => {
    if (autoScoped || loading) return;
    setAutoScoped(true);
    if (!meta.canViewAll && meta.pendingCount > 0 && scope === "all") setScope("pending");
  }, [autoScoped, loading, meta, scope]);

  // Tarih + arama filtresi (durum kartları sayısı bunlara göre hesaplanır)
  const baseFiltered = useMemo(() => {
    const searchText = search.trim().toLocaleLowerCase("tr-TR");
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(`${endDate}T23:59:59`) : null;

    return dofs.filter((dof) => {
      const istekTarih = dof.dofIstekTarih ? new Date(dof.dofIstekTarih) : null;
      if (start && (!istekTarih || istekTarih < start)) return false;
      if (end && (!istekTarih || istekTarih > end)) return false;
      if (searchText) {
        const haystack = [
          dof.dofNo,
          dof.uygunsuzlukTanimi,
          dof.dofiTalepEden,
          dof.kokNeden,
          dof.dofIstenenBirim,
          dof.createdBy?.fullName,
          getAssignedText(dof),
        ]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase("tr-TR");
        if (!haystack.includes(searchText)) return false;
      }
      return true;
    });
  }, [dofs, startDate, endDate, search]);

  const filteredDofs = useMemo(() => {
    if (!statusFilter) return baseFiltered;
    if (statusFilter === "gecikmis") return baseFiltered.filter(isLate);
    return baseFiltered.filter((d) => getDofStatus(d) === statusFilter);
  }, [baseFiltered, statusFilter]);

  const statusCounts = DOF_STATUSES.map((st) => ({
    ...st,
    count: baseFiltered.filter((d) => getDofStatus(d) === st.key).length,
  }));
  const overdueCount = baseFiltered.filter(isLate).length;

  const handleView = (id) => navigate(`/DofDetay/${id}`);

  const handleDelete = async (e, dof) => {
    e.stopPropagation();
    if (!window.confirm(`${dof.dofNo || "Bu"} numaralı DÖF kaydı silinecek (pasife alınacak). Emin misiniz?`)) return;
    try {
      const response = await deleteDofIstek(dof._id);
      alert(response?.data?.message || "DÖF kaydı silindi.");
      fetchDofs(scope);
    } catch (error) {
      console.error("DÖF silme hatası:", error);
      alert(error.response?.data?.message || "DÖF kaydı silinemedi.");
    }
  };

  const handleExcelExport = () => {
    if (!filteredDofs.length) {
      alert("Dışa aktarılacak kayıt yok.");
      return;
    }
    const rows = filteredDofs.map((dof) => ({
      "DÖF NO": dof.dofNo || "",
      DURUM: getStatusMeta(getDofStatus(dof)).label,
      GECİKME: dof._termin?.overdue ? "Termin geçti" : dof._termin?.followUpLate ? "Takip gecikti" : "",
      "İSTEK TARİHİ": formatDofDate(dof.dofIstekTarih),
      "FAALİYET TÜRÜ": FALIYET_LABELS[dof.faliyetTuru] || dof.faliyetTuru || "",
      "TESPİT TÜRÜ": getTespitTuruMetni(dof),
      "UYGUNSUZLUK TANIMI": dof.uygunsuzlukTanimi || "",
      "TALEP EDEN": dof.dofiTalepEden || "",
      "İSTENEN BİRİM AMİRİ": dof.dofIstenenBirim || "",
      SORUMLULAR: getAssignedText(dof, "sorumlu"),
      ONAYCILAR: getAssignedText(dof, "onayci"),
      TAKİPÇİLER: getAssignedText(dof, "takipci"),
      "KÖK NEDEN": dof.kokNeden || "",
      DÜZELTME: dof.duzetmeFaliyetTanimi || "",
      "DÜZELTME SORUMLU": dof.duzeltmeSorumlu || "",
      "DÜZELTME TERMİN": formatDofDate(dof.duzeltmeTerminSure),
      "DÜZELTİCİ FAALİYET": dof.duzelticiFaliyetTanimi || "",
      "DÜZELTİCİ SORUMLU": dof.duzelticiSorumlu || "",
      "DÜZELTİCİ TERMİN": formatDofDate(dof.duzelticiTerminSure),
      "ÖNLEYİCİ FAALİYET": dof.onleyiciciFaliyetTanimi || "",
      "ÖNLEYİCİ SORUMLU": dof.onleyiciciSorumlu || "",
      "ÖNLEYİCİ TERMİN": formatDofDate(dof.onleyiciciTerminSure),
      "TAKİP TARİHİ": formatDofDate(dof.takipTarihi),
      "TAKİBİ GERÇEKLEŞTİREN": dof.takibiGerceklestiren || "",
      "TAKİP SONUCU & ÖNERİ": dof.takipSonucuOneri || "",
      "YENİ DÖF AÇILMALI": dof.yeniDofAcilmali ? "Evet" : "Hayır",
      "DÖF KAPATMA TARİHİ": formatDofDate(dof.dofKapatmaTarihiOnay),
      "YÖNETİM TEMSİLCİSİ": dof.yonetimTemsilcisi || "",
      AÇAN: dof.createdBy?.fullName || "",
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet["!cols"] = Object.keys(rows[0]).map((key) => ({ wch: Math.min(Math.max(key.length + 2, 14), 50) }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "DÖF Listesi");
    XLSX.writeFile(workbook, `DOF_Listesi_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const resetFilters = () => {
    setStartDate("");
    setEndDate("");
    setStatusFilter("");
    setSearch("");
  };

  if (!user) return null;

  const hasFilter = startDate || endDate || statusFilter || search;

  return (
    <Layout>
      <div className="dof-page">
        {/* Hero */}
        <section className="dof-hero">
          <div>
            <span className="dof-kicker">
              <ShieldCheck size={15} /> Kalite · Düzeltici ve Önleyici Faaliyet
            </span>
            <h1>DÖF Yönetimi</h1>
            <p>
              {meta.canViewAll
                ? "Tüm DÖF kayıtlarını, iş akışındaki durumlarını ve termin takibini buradan yönetebilirsiniz."
                : "Açtığınız ve size atanan DÖF kayıtları. Sırası size gelen işler “Bekleyen İşlerim” sekmesinde."}
            </p>
          </div>
          <div className="dof-hero-actions">
            <div className="dof-hero-card" onClick={() => setScope("pending")}>
              <span>Bekleyen İşlerim</span>
              <strong>{meta.pendingCount ?? "–"}</strong>
              <small>işlem yapmanız gereken DÖF</small>
            </div>
            {meta.canCreate && (
              <button type="button" className="dof-btn dof-btn-light" onClick={() => navigate("/DofiWizard")}>
                <Plus size={17} /> Yeni DÖF Aç
              </button>
            )}
          </div>
        </section>

        {/* Durum kartları */}
        <div className="dof-stat-grid">
          {statusCounts.map((st) => {
            const Icon = STATUS_ICONS[st.key];
            return (
              <button
                type="button"
                key={st.key}
                className={`dof-stat-card ${statusFilter === st.key ? "active" : ""}`}
                onClick={() => setStatusFilter(statusFilter === st.key ? "" : st.key)}
              >
                <span className={`dof-stat-icon st-${st.key}`}>
                  <Icon size={21} />
                </span>
                <div>
                  <small>{st.label}</small>
                  <strong>{st.count}</strong>
                  <em>{st.long}</em>
                </div>
              </button>
            );
          })}
          <button
            type="button"
            className={`dof-stat-card ${statusFilter === "gecikmis" ? "active" : ""}`}
            onClick={() => setStatusFilter(statusFilter === "gecikmis" ? "" : "gecikmis")}
          >
            <span className="dof-stat-icon st-gecikmis">
              <AlertTriangle size={21} />
            </span>
            <div>
              <small>Geciken</small>
              <strong style={{ color: overdueCount ? "var(--danger)" : undefined }}>{overdueCount}</strong>
              <em>termin / takip gecikmesi</em>
            </div>
          </button>
        </div>

        {/* Liste paneli */}
        <section className="dof-panel">
          <div className="dof-panel-header" style={{ flexWrap: "wrap" }}>
            <div className="dof-tabs">
              {SCOPES.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    type="button"
                    key={item.key}
                    className={`dof-tab ${scope === item.key ? "active" : ""}`}
                    onClick={() => setScope(item.key)}
                  >
                    <Icon size={15} />
                    {item.key === "all" && !meta.canViewAll ? "Erişimim Olanlar" : item.label}
                    {item.key === "pending" && meta.pendingCount > 0 && (
                      <span className="dof-tab-count">{meta.pendingCount}</span>
                    )}
                  </button>
                );
              })}
            </div>
            <button type="button" className="dof-btn dof-btn-secondary dof-btn-sm" onClick={handleExcelExport}>
              <FileSpreadsheet size={15} /> Excel
            </button>
          </div>

          <div className="dof-filter-bar">
            <div className="dof-input-icon">
              <Search size={15} />
              <input
                type="text"
                placeholder="DÖF no, uygunsuzluk, kişi ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <input type="date" title="Başlangıç" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <input type="date" title="Bitiş" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">Tüm durumlar</option>
              {DOF_STATUSES.map((st) => (
                <option key={st.key} value={st.key}>
                  {st.label}
                </option>
              ))}
              <option value="gecikmis">Gecikenler</option>
            </select>
            <button type="button" className="dof-btn dof-btn-secondary" disabled={!hasFilter} onClick={resetFilters}>
              Temizle
            </button>
          </div>

          {loading ? (
            <div className="dof-loading">
              <div className="dof-spinner" />
              <div>DÖF kayıtları yükleniyor...</div>
            </div>
          ) : filteredDofs.length === 0 ? (
            <div className="dof-empty">
              <Inbox size={42} />
              <div style={{ fontWeight: 700, color: "var(--ink)" }}>
                {scope === "pending" && !hasFilter ? "Bekleyen işiniz yok " : "Kayıt bulunamadı"}
              </div>
              <small>
                {scope === "pending" && !hasFilter
                  ? "Size atanan DÖF'lerde sıranız geldiğinde burada görünecek."
                  : "Filtreleri değiştirerek tekrar deneyin."}
              </small>
            </div>
          ) : (
            <div className="dof-table-wrap">
              <table className="dof-table">
                <thead>
                  <tr>
                    <th>DÖF</th>
                    <th>Durum</th>
                    <th>Uygunsuzluk</th>
                    <th>Sorumlu</th>
                    <th>Onaycı / Takipçi</th>
                    <th>Termin</th>
                    <th>Açan</th>
                    <th style={{ textAlign: "right" }}>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDofs.map((dof) => {
                    const status = getDofStatus(dof);
                    const statusMeta = getStatusMeta(status);
                    const myTurn = dof._access?.myTurn;
                    const sorumlular = (dof.assignedUsers || []).filter((u) => u.assignmentType === "sorumlu");
                    const digerleri = (dof.assignedUsers || []).filter((u) =>
                      ["onayci", "takipci"].includes(u.assignmentType)
                    );
                    return (
                      <tr key={dof._id} className={myTurn ? "my-turn" : ""} onClick={() => handleView(dof._id)}>
                        <td>
                          <span className="dof-no">{dof.dofNo || "-"}</span>
                          <span className="dof-sub">
                            {formatDofDate(dof.dofIstekTarih)}
                            {dof.faliyetTuru ? ` · ${FALIYET_LABELS[dof.faliyetTuru] || dof.faliyetTuru}` : ""}
                          </span>
                        </td>
                        <td>
                          <span className={`dof-badge st-${status}`}>{statusMeta.label}</span>
                          {myTurn && (
                            <div style={{ marginTop: 5 }}>
                              <span className="dof-badge dof-badge-dark">Sıra sizde</span>
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="dof-clamp" title={dof.uygunsuzlukTanimi || ""}>
                            {dof.uygunsuzlukTanimi || "-"}
                          </div>
                          {getTespitTuruMetni(dof) && <span className="dof-sub">{getTespitTuruMetni(dof)}</span>}
                        </td>
                        <td>
                          {sorumlular.length
                            ? sorumlular.map((u, i) => (
                                <span className="dof-chip" key={`${u.email}-${i}`}>
                                  <span className="dof-avatar role-sorumlu">{initials(u.fullName)}</span>
                                  {u.fullName}
                                </span>
                              ))
                            : <span className="dof-sub">Atanmadı</span>}
                        </td>
                        <td>
                          {digerleri.length
                            ? digerleri.map((u, i) => (
                                <span className="dof-chip" key={`${u.email}-${i}`} title={ASSIGNMENT_LABELS[u.assignmentType]}>
                                  <span className={`dof-avatar role-${u.assignmentType}`}>{initials(u.fullName)}</span>
                                  {u.fullName}
                                </span>
                              ))
                            : <span className="dof-sub">Açan kişi</span>}
                        </td>
                        <td>
                          <TerminCell dof={dof} />
                        </td>
                        <td>
                          {dof.createdBy?.fullName || dof.dofiTalepEden || "-"}
                          {dof._access?.myAssignmentType && (
                            <span className="dof-sub">Rolünüz: {ASSIGNMENT_LABELS[dof._access.myAssignmentType]}</span>
                          )}
                        </td>
                        <td>
                          <div className="dof-row-actions">
                            {myTurn ? (
                              <button
                                type="button"
                                className="dof-btn dof-btn-warning dof-btn-sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleView(dof._id);
                                }}
                              >
                                İşlem Yap
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="dof-icon-btn"
                                title="Görüntüle"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleView(dof._id);
                                }}
                              >
                                <Eye size={16} />
                              </button>
                            )}
                            {dof._access?.canEdit && (
                              <button
                                type="button"
                                className="dof-icon-btn"
                                title="Tüm alanları düzenle"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/DofEdit/${dof._id}`);
                                }}
                              >
                                <Pencil size={16} />
                              </button>
                            )}
                            {dof._access?.canDelete && (
                              <button type="button" className="dof-icon-btn danger" title="Sil" onClick={(e) => handleDelete(e, dof)}>
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}

export default DofList;
